// src/pages/ScrollToggle.tsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import FloatingActionButtons from "../components/FloatingActionButtons/FloatingActionButtons";
import { useThemeColors } from "../constants/theme";

const ScrollButton = styled.button<{ colors: any }>`
  position: fixed;
  bottom: clamp(20px, 5vh, 30px);
  right: clamp(15px, 3vw, 25px);
  width: clamp(25px, 5vw, 50px);
  height: clamp(25px, 5vw, 50px);
  min-width: clamp(25px, 5vw, 50px);
  min-height: clamp(25px, 5vw, 50px);
  max-width: 50px;
  max-height: 50px;
  border-radius: 50%;
  background: ${(props) => props.colors.orange};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(16px, 3vw, 28px);
  z-index: 999;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-card);

  &:hover {
    background: ${(props) => props.colors.orangeAlt};
    transform: translateY(-3px);
    box-shadow: var(--shadow-hover);
  }

  &:active {
    transform: translateY(0);
  }

  /* 4K and Ultra-wide screens */
  @media (min-width: 1920px) {
    bottom: 30px;
    right: 30px;
    width: 50px;
    height: 50px;
    min-width: 50px;
    min-height: 50px;
    font-size: 26px;
  }

  /* Large Desktop */
  @media (min-width: 1440px) and (max-width: 1919px) {
    bottom: 28px;
    right: 30px;
    width: 45px;
    height: 45px;
    min-width: 45px;
    min-height: 45px;
    font-size: 24px;
  }

  /* Desktop */
  @media (min-width: 1024px) and (max-width: 1439px) {
    bottom: 25px;
    right: 20px;
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    font-size: 21px;
  }

  /* Tablet */
  @media (min-width: 768px) and (max-width: 1023px) {
    bottom: 22px;
    right: 20px;
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    font-size: 19px;
  }

  /* Mobile Large */
  @media (min-width: 481px) and (max-width: 767px) {
    bottom: 20px;
    right: 15px;
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    font-size: 16px;
  }

  /* Mobile Medium */
  @media (min-width: 361px) and (max-width: 480px) {
    bottom: 18px;
    right: 15px;
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    font-size: 14px;
  }

  /* Mobile Small */
  @media (max-width: 360px) {
    bottom: 15px;
    right: 15px;
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    font-size: 12px;
  }
`;

const ScrollToggle: React.FC = () => {
  const [isAtBottom, setIsAtBottom] = useState(false);
  const colors = useThemeColors();

  useEffect(() => {
    const checkScrollPosition = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const totalHeight = document.documentElement.scrollHeight;
      const isBottom = Math.abs(scrollPosition - totalHeight) < 50;
      setIsAtBottom(isBottom);
    };

    checkScrollPosition();

    let ticking = false;
    const throttledCheck = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          checkScrollPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledCheck, { passive: true });

    return () => {
      window.removeEventListener("scroll", throttledCheck);
    };
  }, []);

  const handleClick = () => {
    if (isAtBottom) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <FloatingActionButtons />
      <ScrollButton onClick={handleClick} colors={colors}>
        {isAtBottom ? <FaArrowUp /> : <FaArrowDown />}
      </ScrollButton>
    </>
  );
};

export default ScrollToggle;
